import { LOCALES, type Locale } from '@qm/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../config';
import { Logo } from '../components/Icon';
import { useLocations } from '../lib/queries';
import { Button, ErrorState, Spinner, errorMessage, useToast } from '../components/ui';
import { api, type LocationNode, type Me } from '../lib/api';
import { useAuth, useMe } from '../lib/auth';
import { setLanguage } from '../i18n';
import { LANGUAGE_NAMES } from './System';

/** Первый запуск: выбор языка и региона (обязательные требования ТЗ). */
export function OnboardingPage() {
  const { t } = useTranslation();
  const me = useMe();
  const { setMe } = useAuth();
  const toast = useToast();
  const [step, setStep] = useState<'language' | 'region'>('language');
  const [locale, setLocale] = useState<Locale>(me.settings.locale);
  const [busy, setBusy] = useState(false);
  const locations = useLocations();

  // Предлагаем города/районы первого активного региона (Каракалпакстан) плоским списком.
  const flatten = (nodes: LocationNode[]): LocationNode[] => nodes.flatMap((n) => (n.type === 'country' || n.type === 'region' ? flatten(n.children) : [n]));
  const places = flatten(locations.data ?? []);

  const finish = async (locationId: string | null) => {
    setBusy(true);
    try {
      const m = await api<Me>('/users/me/settings', { method: 'PATCH', json: { locale, locationId, onboardingCompleted: true } });
      setMe(m);
    } catch (e) {
      toast(errorMessage(t, e), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="onboarding">
      <div className="brand brand-lg">
        <Logo size={56} />
        <span className="brand-name">{config.appName}</span>
      </div>
      {step === 'language' ? (
        <>
          <h1>{t('onboarding.chooseLanguage')}</h1>
          <div className="stack">
            {(me.availableLocales?.length ? me.availableLocales : LOCALES).map((l) => (
              <button
                key={l}
                type="button"
                className={`option ${locale === l ? 'option-active' : ''}`}
                onClick={() => {
                  setLocale(l);
                  void setLanguage(l);
                }}
              >
                {LANGUAGE_NAMES[l]}
              </button>
            ))}
          </div>
          <Button size="lg" block onClick={() => setStep('region')}>
            {t('common.next')}
          </Button>
        </>
      ) : (
        <>
          <h1>{t('onboarding.chooseRegion')}</h1>
          <p className="muted">{t('onboarding.regionHint')}</p>
          {locations.isLoading ? (
            <Spinner />
          ) : locations.error ? (
            <ErrorState error={locations.error} onRetry={() => void locations.refetch()} />
          ) : (
            <div className="option-list">
              {places.map((p) => (
                <button key={p.id} type="button" className="option" disabled={busy} onClick={() => void finish(p.id)}>
                  {p.name}
                </button>
              ))}
            </div>
          )}
          <Button variant="ghost" block disabled={busy} onClick={() => void finish(null)}>
            {t('onboarding.skipRegion')}
          </Button>
        </>
      )}
    </div>
  );
}
